import React, { useState, useEffect, useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  MutateStartTvEnergyStream,
  MutateStopTvEnergyStream,
} from "../../../utils/graphql/hooks/TVEnergyHooks";
import { getColourMap } from "../../../utils";
import { createSelector } from "@reduxjs/toolkit";

function getRelativeTime(timestamp: number, currentTime: number): string {
  const now = currentTime;
  const diffMs = now - timestamp;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 5) {
    return "LIVE";
  } else if (diffSeconds < 60) {
    return `${diffSeconds}s`;
  } else {
    return `${diffMinutes}m ${diffSeconds % 60}s`;
  }
}

export default function EnergyTable() {
  const [currentTime, setCurrentTime] = useState(Date.now());

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  // Get driver short names for red and blue cars
  const redCarDriver = jagDrivers?.redCar;
  const blueCarDriver = jagDrivers?.blueCar;
  const redCarName = redCarDriver
    ? driverInfo[redCarDriver]?.driverName || redCarDriver
    : "Red";
  const blueCarName = blueCarDriver
    ? driverInfo[blueCarDriver]?.driverName || blueCarDriver
    : "Blue";

  const standingsSelector = createSelector(
    (state: RootState) =>
      state.aks.v2.timing?.session?.standings?.overall?.active,
    (standings) => standings || {},
  );

  const standings = useSelector(standingsSelector);

  const standingsByDriver: Record<string, number> = useMemo(() => {
    const result: Record<string, number> = {};
    for (const position in standings) {
      const driver = standings[position as keyof typeof standings]?.participant;
      if (driver != null) {
        result[driver] = parseInt(position, 10);
      }
    }
    return result;
  }, [standings]);

  const tvEnergyData = useSelector(
    (state: RootState) => state.tvEnergy?.data,
    isEqual,
  );

  const connectionData = useSelector(
    (state: RootState) => state.tvEnergy?.connectionState,
    isEqual,
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const [streamLink, setStreamLink] = useState(
    connectionData?.streamLink || "",
  );

  useEffect(() => {
    if (connectionData?.streamLink) {
      setStreamLink(connectionData.streamLink);
    }
  }, [connectionData?.streamLink]);

  const startTvEnergyStream = MutateStartTvEnergyStream();
  const stopTvEnergyStream = MutateStopTvEnergyStream();

  const handleStartStream = async () => {
    try {
      await startTvEnergyStream({
        variables: { streamLink: streamLink },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleStopStream = async () => {
    try {
      await stopTvEnergyStream();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.tvEnergyControls}>
        <input
          type="text"
          className={styles.streamLinkInput}
          placeholder="Stream Link"
          value={streamLink}
          onChange={(e) => setStreamLink(e.target.value)}
        />
        {connectionData?.isConnected && (
          <button className={styles.resetButton} onClick={handleStopStream}>
            Stop
          </button>
        )}
        {!connectionData?.isConnected && (
          <button className={styles.resetButton} onClick={handleStartStream}>
            Start
          </button>
        )}
        <div className={styles.timingFeedOffset}>
          {connectionData?.timingFeedOffset} s
        </div>
        <div className={styles.connectionStatus}>
          <span
            style={{ color: connectionData?.isConnected ? "green" : "red" }}
          >
            {connectionData?.isConnected ? "LIVE" : "OFF"}
          </span>
        </div>
      </div>
      <div className={styles.energyTable}>
        <table>
          <thead>
            <tr>
              <th>Drv</th>
              <th>TV</th>
              <th>FL %</th>
              <th>Trgt</th>
              <th
                style={{
                  color: redCarDriver
                    ? driverInfo[redCarDriver]?.driverHighlightColourAlt
                    : undefined,
                }}
              >
                Δ {redCarName}
              </th>
              <th
                style={{
                  color: blueCarDriver
                    ? driverInfo[blueCarDriver]?.driverHighlightColourAlt
                    : undefined,
                }}
              >
                Δ {blueCarName}
              </th>
              <th>Δ Lead</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(tvEnergyData || {})
              .sort(([driverA], [driverB]) => {
                const positionA = standingsByDriver[driverA] ?? 999;
                const positionB = standingsByDriver[driverB] ?? 999;
                return positionA - positionB;
              })
              .map(([driver, energyInfo]) => {
                const driverInfoEntry =
                  driverInfo[driver as keyof typeof driverInfo];

                const driverName = energyInfo.driverName;
                const style = {
                  color: driverInfoEntry?.driverFontColour || "black",
                  backgroundColor: driverInfoEntry?.driverTeamColour || "white",
                };
                return (
                  <tr key={driver} style={style}>
                    <td>{driverName}</td>
                    <td>
                      {energyInfo.tvPercent ? energyInfo.tvPercent : "N/A"}
                    </td>
                    <td>
                      {energyInfo.processedPercent
                        ? `${energyInfo.processedPercent}%`
                        : "N/A"}
                    </td>
                    <td>
                      {energyInfo.estimatedTarget != null
                        ? energyInfo.estimatedTarget.toFixed(2)
                        : "N/A"}
                    </td>
                    <td
                      style={{
                        backgroundColor:
                          energyInfo.deltaToRedCar != null
                            ? getColourMap(energyInfo.deltaToRedCar ?? 0, 3, -3)
                            : "white",
                        color: "black",
                      }}
                    >
                      {energyInfo.deltaToRedCar != null
                        ? `${
                            energyInfo.deltaToRedCar >= 0 ? "+" : ""
                          }${energyInfo.deltaToRedCar.toFixed(2)}%`
                        : "N/A"}
                    </td>
                    <td
                      style={{
                        backgroundColor:
                          energyInfo.deltaToBlueCar != null
                            ? getColourMap(
                                energyInfo.deltaToBlueCar ?? 0,
                                3,
                                -3,
                              )
                            : "white",
                        color: "black",
                      }}
                    >
                      {energyInfo.deltaToBlueCar != null
                        ? `${
                            energyInfo.deltaToBlueCar >= 0 ? "+" : ""
                          }${energyInfo.deltaToBlueCar.toFixed(2)}%`
                        : "N/A"}
                    </td>
                    <td
                      style={{
                        backgroundColor:
                          energyInfo.deltaToLeader != null
                            ? getColourMap(energyInfo.deltaToLeader ?? 0, 3, -3)
                            : "white",
                        color: "black",
                      }}
                    >
                      {energyInfo.deltaToLeader != null
                        ? `${
                            energyInfo.deltaToLeader >= 0 ? "+" : ""
                          }${energyInfo.deltaToLeader.toFixed(2)}%`
                        : "N/A"}
                    </td>
                    <td>
                      {energyInfo.timestamp
                        ? getRelativeTime(energyInfo.timestamp, currentTime)
                        : "N/A"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
